import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, MessageCircle, Play, X } from 'lucide-react';

const steps = [
  {
    step: 1,
    title: 'Apply',
    description: 'Fill out the application form with your details',
    icon: FileText,
    color: 'blue'
  },
  {
    step: 2,
    title: 'Upload ID',
    description: 'Submit your Student ID or NYSC ID for verification',
    icon: Upload,
    color: 'green'
  },
  {
    step: 3,
    title: 'Join Telegram',
    description: 'Get added to our exclusive training group',
    icon: MessageCircle,
    color: 'purple'
  },
  {
    step: 4,
    title: 'Start Training',
    description: 'Begin your 6-week export journey',
    icon: Play,
    color: 'orange'
  }
];

const C2CHowToJoin = () => {
  const [isRegistrationOpen, setIsRegistrationOpen] = React.useState(false);
  const [iframeError, setIframeError] = React.useState(false);
  const [iframeLoaded, setIframeLoaded] = React.useState(false);

  const handleApplyNow = () => {
    setIsRegistrationOpen(true);
    setIframeError(false);
    setIframeLoaded(false);
    
    // Set a timeout to detect if iframe fails to load within 10 seconds
    setTimeout(() => {
      if (!iframeLoaded && !iframeError) {
        setIframeError(true);
        console.log('Iframe loading timeout - likely blocked by Google');
      }
    }, 10000);
  };

  const handleIframeError = () => {
    setIframeError(true);
    console.log('Google Form iframe failed to load');
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    console.log('Google Form iframe loaded successfully');
  };

  return (
    <>
      <section id="how-to-join" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 sm:text-4xl"
          >
            How to Join
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-xl text-gray-600"
          >
            Simple steps to start your export journey
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="text-center">
                <div className={`w-16 h-16 bg-${step.color}-100 rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <step.icon className={`h-8 w-8 text-${step.color}-600`} />
                </div>
                
                <div className={`inline-flex items-center justify-center w-8 h-8 bg-${step.color}-600 text-white rounded-full text-sm font-bold mb-4`}>
                  {step.step}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <div className="w-8 h-0.5 bg-gray-300" />
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-300 border-t-2 border-b-2 border-t-transparent border-b-transparent" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
            <p className="text-gray-600 mb-6">
              Join hundreds of students and NYSC members who are already building their export careers.
            </p>
            <button 
              onClick={handleApplyNow}
              className="inline-flex items-center px-8 py-4 bg-green-500 text-white rounded-lg font-semibold text-lg hover:bg-green-600 transition-all duration-300 transform hover:scale-105"
            >
              Apply Now - It's Free!
            </button>
          </div>
        </motion.div>
      </div>

      {/* Registration Modal with Google Form */}
      {isRegistrationOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                <button
                  onClick={() => setIsRegistrationOpen(false)}
                  title="Close registration form"
                  aria-label="Close registration form"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none shadow-lg p-2"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                    Apply for Campus2Container
                  </h3>
                  <p className="text-gray-600 mb-6 text-center">
                    Fill out the form below to join our free export training program
                  </p>

                  {/* Google Form Iframe with Enhanced Fallback */}
                  <div className="w-full h-[600px] border rounded-lg overflow-hidden bg-gray-50 relative">
                    {!iframeLoaded && !iframeError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading registration form...</p>
                        </div>
                      </div>
                    )}
                    
                    {iframeError ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center max-w-md mx-auto p-6">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                            <h4 className="font-semibold text-yellow-800 mb-2">Form Loading Issue</h4>
                            <p className="text-yellow-700 text-sm mb-4">
                              The registration form cannot be displayed in this popup due to browser security restrictions.
                            </p>
                            <a 
                              href="https://docs.google.com/forms/d/e/1FAIpQLScoe_4DWHIa7wkJFtX_htwdF5UOMxyvoEezhYq3P3Ba3rWdlA/viewform" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                              onClick={() => setIsRegistrationOpen(false)}
                            >
                              Open Registration Form
                              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src="https://docs.google.com/forms/d/e/1FAIpQLScoe_4DWHIa7wkJFtX_htwdF5UOMxyvoEezhYq3P3Ba3rWdlA/viewform?embedded=true"
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        marginHeight={0}
                        marginWidth={0}
                        title="Campus2Container Registration Form"
                        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                        className="border-none"
                      />
                    )}
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      Having trouble with the form? 
                      <a 
                        href="https://docs.google.com/forms/d/e/1FAIpQLScoe_4DWHIa7wkJFtX_htwdF5UOMxyvoEezhYq3P3Ba3rWdlA/viewform?usp=header" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 ml-1"
                      >
                        Open in new tab
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
      </>
  );
};

export default C2CHowToJoin;